/* Every trade the book holds, which until now nobody could look at.
 *
 * Trades were written by Reduce, by the CSV import and by the broker
 * connection, and then read only by the rule engine and the tax report. There
 * was no screen. A sale you recorded went into a store you could not open,
 * check, correct or remove — and the year's tax figures are built out of
 * exactly those rows, so "the report says 4,180" had no answer to "from what?".
 *
 * Three things follow from that, and they are the whole of this file:
 *
 * It lists them. It lets one be corrected, because a price typed wrong is a
 * wrong gain in a return. And it takes one by hand, because a trade that
 * happened before this book existed — the SKHY contract closed before the seed,
 * the sale at a broker whose CSV you no longer have — has never had a door.
 *
 * What it deliberately does not do is move your holdings. Recording a sale in
 * Reduce does two things at once: it writes down what happened and it takes the
 * shares out of the book. Here only the first half applies. A row corrected
 * here restates the record of a trade; it does not re-run the trade. Said
 * plainly on screen, because the two are easy to conflate and expensive to get
 * backwards. */

import { useMemo, useState } from 'react';
import { accountName } from '../lib/accounts';
import { moneyExact, moneyExactSigned, shares as fmtShares, shortDate, todayIso , isDay} from '../lib/format';
import { newId } from '../lib/id';
import { decimal, decimalOr, isPositiveDecimal } from '../lib/number';
import { useStore } from '../state/store';
import { effectOf, moveCash, restating, reverse, settle, tradeCashDelta } from '../state/cashMove';
import { lotFollowingTrades } from '../state/basisFromTrades';
import type { Account, Trade, TradeReason } from '../types';

const REASONS: Array<{ value: TradeReason | ''; label: string }> = [
  { value: '', label: 'not recorded' },
  { value: 'rebuild_cash', label: 'rebuild cash' },
  { value: 'rotate', label: 'rotate into another name' },
  { value: 'thesis_broke', label: 'the thesis broke' },
  { value: 'trim_to_limit', label: 'trim to the limit' },
  { value: 'other', label: 'other' },
];

export function TradeLog({ say }: { say: (m: string) => void }) {
  const { state } = useStore();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const rows = useMemo(
    /* Newest first. Trades entered for the same day carry the same moment, and
       a sale comes before the purchase it paid for, so of those the purchase is
       listed on top — the same reading as the History screen, see
       `newestFirst` in state/activity.ts. */
    () => [...state.data.trades].sort((a, b) =>
      b.at.localeCompare(a.at) || (a.side === b.side ? 0 : a.side === 'buy' ? -1 : 1)),
    [state.data.trades],
  );

  return (
    <section>
      <div className="section-head">
        <h2>Every trade</h2>
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => { setAdding(true); setEditing(null); }}>
          Record one by hand
        </button>
      </div>

      {adding ? (
        <TradeForm say={say} onDone={() => setAdding(false)} />
      ) : null}

      {rows.length === 0 ? (
        <div className="card card-pad">
          <p style={{ fontSize: 13.5, lineHeight: 1.55 }}>No trades recorded.</p>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 8 }}>
            Selling through <strong>Reduce</strong> writes one, and so does importing a broker&rsquo;s
            file. Anything older than this book, such as a sale at a broker whose export you no longer
            have, a contract closed before you started, goes in by hand above. The figures on this
            screen are built out of these rows and nothing else, so a year with none reads as a
            year with no gains rather than a year nobody recorded.
          </p>
        </div>
      ) : (
        <div className="rows">
          {rows.map((t) => (
            editing === t.id ? (
              <TradeForm key={t.id} trade={t} say={say} onDone={() => setEditing(null)} />
            ) : (
              <TradeRow key={t.id} trade={t} onEdit={() => { setEditing(t.id); setAdding(false); }} />
            )
          ))}
        </div>
      )}
    </section>
  );
}

function TradeRow({ trade, onEdit }: { trade: Trade; onEdit: () => void }) {
  const { state } = useStore();
  const account = accountName(state.data.accounts, trade.account);

  return (
    <div className="card card-pad">
      <div className="row-top">
        <span className="row-ticker" style={{ fontSize: 13.5 }}>{trade.ticker}</span>
        <span className="tag" data-kind={trade.side === 'sell' ? 'muted' : 'ok'}>
          {trade.side.toUpperCase()}
        </span>
        <span className="row-right">
          <span className="num" style={{ fontWeight: 600 }}>
            {fmtShares(trade.shares)} @ {moneyExact(trade.price)}
          </span>
          <span className="num dim" style={{ display: 'block', fontSize: 11.5, textAlign: 'right' }}>
            {moneyExact(trade.shares * trade.price)}
          </span>
        </span>
      </div>
      <div className="row-meta">
        <span>{shortDate(trade.at)}</span>
        <span>{account}</span>
        {trade.side === 'sell' ? (
          typeof trade.realizedGain === 'number' ? (
            <span className={`num ${trade.realizedGain >= 0 ? 'up' : 'down'}`}>
              {moneyExactSigned(trade.realizedGain)} realised
            </span>
          ) : (
            /* Not a zero. A sale with no gain recorded is counted apart
               everywhere else in this book, so it says so here too. */
            <span className="dim">no gain recorded</span>
          )
        ) : null}
      </div>
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={onEdit}>
        Correct this trade
      </button>
    </div>
  );
}

/** One form for both jobs. Recording and correcting ask for the same facts, and
    a second form would be a second place for them to drift apart. */
export function TradeForm({ trade, say, onDone }: {
  trade?: Trade;
  say: (m: string) => void;
  onDone: () => void;
}) {
  const { state, book, putTrade, removeRecord, setCash, putHolding } = useStore();
  const accounts = state.data.accounts;

  const [ticker, setTicker] = useState(trade?.ticker ?? '');
  const [account, setAccount] = useState<Account>(trade?.account ?? accounts[0]?.id ?? '');
  const [side, setSide] = useState<'buy' | 'sell'>(trade?.side ?? 'sell');
  const [shares, setShares] = useState(trade ? String(trade.shares) : '');
  const [price, setPrice] = useState(trade ? String(trade.price) : '');
  const [at, setAt] = useState((trade?.at ?? todayIso()).slice(0, 10));
  const [gain, setGain] = useState(
    typeof trade?.realizedGain === 'number' ? String(trade.realizedGain) : '',
  );
  const [fee, setFee] = useState(typeof trade?.fee === 'number' ? String(trade.fee) : '');
  const [reason, setReason] = useState<TradeReason | ''>(trade?.reason ?? '');
  const [destination, setDestination] = useState(trade?.destination ?? '');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);

  const clean = ticker.trim().toUpperCase();
  const ready = clean !== '' && account !== '' && isPositiveDecimal(shares) && isPositiveDecimal(price)
    && isDay(at);

  /** Bring each named lot's average back in line with its trades, where they
      account for the whole of it. Silent when they cannot say. */
  const followTrades = async (
    all: Trade[],
    pairs: Array<{ ticker: string; account: Account } | null>,
  ) => {
    const done = new Set<string>();
    for (const pair of pairs) {
      if (!pair) continue;
      const key = `${pair.ticker}|${pair.account}`;
      if (done.has(key)) continue;
      done.add(key);
      const lot = state.data.holdings.find(
        (h) => h.ticker === pair.ticker && h.account === pair.account,
      );
      if (!lot) continue;
      const next = lotFollowingTrades(all, lot);
      if (next) await putHolding(next);
    }
  };

  const save = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const next: Trade = {
        id: trade?.id ?? newId('trade'),
        ticker: clean,
        account,
        side,
        shares: decimal(shares),
        price: decimal(price),
        // Built the same way as the other forms rather than parsed and
        // re-serialised: `new Date('T12:00:00Z')` throws and loses the form.
        at: `${at}T12:00:00.000Z`,
      };
      /* A gain only means anything on a sale, and a blank one stays blank —
         a zero would report the sale as breaking even, which is a claim. */
      if (side === 'sell' && gain.trim() !== '' && Number.isFinite(decimal(gain))) {
        next.realizedGain = decimalOr(gain);
      }
      const feePaid = Math.abs(decimalOr(fee));
      if (feePaid > 0) next.fee = feePaid;
      if (reason !== '') next.reason = reason;
      if (reason === 'rotate' && destination.trim() !== '') next.destination = destination.trim();

      /* A fill written down here moves the account's cash the way the fill
         moved it: out for a buy, in for a sale. Correcting one re-states that
         movement — whatever it did before is undone and what it says now is
         applied — so the balance never drifts away from the log. */
      const wanted = effectOf(moveCash(
        book.cashByAccount, state.data.accounts, account,
        tradeCashDelta(side, next.shares, next.price, next.fee ?? 0),
        book.positions.find((p) => p.ticker === clean)?.currency ?? 'USD',
        state.fx,
      ));
      // A trade that never moved a balance goes on not moving one — see
      // `restating` in state/cashMove.ts.
      const effect = trade && !trade.cashEffect ? undefined : wanted;
      if (effect) next.cashEffect = effect;

      await putTrade(next);
      const moves = trade
        ? settle(book.cashByAccount, restating(trade.cashEffect, effect))
        : settle(book.cashByAccount, [effect]);
      for (const m of moves) await setCash(m.account, m.amount, m.currency);

      /* The lot's average is a summary of these trades wherever they account
         for the whole of it, and a summary that does not move when the thing it
         summarises is corrected is stale. Both the pair this trade was on and
         the pair it is on now, because either can have been changed. */
      const after = [...state.data.trades.filter((t) => t.id !== next.id), next];
      await followTrades(after, [
        trade ? { ticker: trade.ticker, account: trade.account } : null,
        { ticker: next.ticker, account: next.account },
      ]);

      // The movement just made, which on a correction is the difference.
      const moved = moves.find((m) => m.account === account);
      say(moved
        ? `${clean} trade ${trade ? 'corrected' : 'recorded'}. ${accountName(state.data.accounts, account)}’s free cash moved by ${moneyExactSigned(moved.applied, moved.currency as 'USD')}.`
        : `${clean} trade ${trade ? 'corrected' : 'recorded'}.`);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!trade) return;
    setBusy(true);
    try {
      await removeRecord('trades', trade.id);
      // Taking the record out takes back what recording it did to the balance.
      for (const m of settle(book.cashByAccount, [reverse(trade.cashEffect)])) {
        await setCash(m.account, m.amount, m.currency);
      }
      await followTrades(
        state.data.trades.filter((t) => t.id !== trade.id),
        [{ ticker: trade.ticker, account: trade.account }],
      );
      say(`${trade.ticker} trade removed from the log.`);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  const known = book.positions.map((p) => p.ticker);

  return (
    <div className="card card-pad">
      <div className="section-head" style={{ marginBottom: 10 }}>
        <h2 style={{ fontSize: 13 }}>{trade ? 'Correcting a trade' : 'Recording a trade'}</h2>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor="tl-ticker">Ticker</label>
          <input id="tl-ticker" className="input" data-mono="true" list="tl-known"
                 autoCapitalize="characters" spellCheck={false}
                 value={ticker} onChange={(e) => setTicker(e.target.value)} />
          <datalist id="tl-known">
            {known.map((k) => <option key={k} value={k} />)}
          </datalist>
        </div>
        <div className="field" style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor="tl-side">Side</label>
          <select id="tl-side" className="select" value={side}
                  onChange={(e) => setSide(e.target.value as 'buy' | 'sell')}>
            <option value="sell">Sell</option>
            <option value="buy">Buy</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor="tl-shares">Shares</label>
          <input id="tl-shares" className="input" data-mono="true" inputMode="decimal"
                 value={shares} onChange={(e) => setShares(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor="tl-price">Price</label>
          <input id="tl-price" className="input" data-mono="true" inputMode="decimal"
                 value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1.3, minWidth: 0 }}>
          <label htmlFor="tl-account">Account</label>
          <select id="tl-account" className="select" value={account}
                  onChange={(e) => setAccount(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor="tl-date">Date</label>
          <input id="tl-date" className="input" type="date"
                 value={at} onChange={(e) => setAt(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label htmlFor="tl-fee">Commission</label>
        <input id="tl-fee" className="input" data-mono="true" inputMode="decimal"
               placeholder="0.00" value={fee} onChange={(e) => setFee(e.target.value)} />
        <div className="hint">
          What the broker took, in the listing&rsquo;s currency. It moves the cash with the rest of
          the trade. On a buy it also belongs in the lot&rsquo;s average cost, and correcting that is
          done on the position, since this log records fills and the lot records what they came to.
        </div>
      </div>

      {side === 'sell' ? (
        <>
          <div className="field">
            <label htmlFor="tl-gain">Realised gain</label>
            <input id="tl-gain" className="input" data-mono="true" inputMode="decimal"
                   placeholder="leave blank if you do not know it"
                   value={gain} onChange={(e) => setGain(e.target.value)} />
            <div className="hint">
              In the account&rsquo;s own currency, and negative for a loss. Blank stays blank: the
              tax figures count a sale with no gain recorded apart from the total rather than as a
              sale that broke even.
            </div>
          </div>

          <div className="field">
            <label htmlFor="tl-reason">Where the money went</label>
            <select id="tl-reason" className="select" value={reason}
                    onChange={(e) => setReason(e.target.value as TradeReason | '')}>
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {reason === 'rotate' ? (
            <div className="field">
              <label htmlFor="tl-dest">Into what</label>
              <input id="tl-dest" className="input" data-mono="true"
                     value={destination} onChange={(e) => setDestination(e.target.value)} />
            </div>
          ) : null}
        </>
      ) : null}

      <div className="note" style={{ marginTop: 4 }}>
        This is the record of a trade, not the trade. Nothing here moves shares in or out of the
        book. Use <strong>Reduce</strong> on the position for a sale that should also change what
        you hold.
      </div>

      <div className="fab-row" style={{ marginTop: 10 }}>
        <button className="btn btn-block" onClick={onDone}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!ready || busy}
                onClick={() => void save()}>
          {busy ? 'Saving…' : trade ? 'Save the correction' : 'Record it'}
        </button>
      </div>

      {trade ? (
        <>
          <div className="divider" />
          {confirmRemove ? (
            <>
              <div className="note" data-tone="crit">
                Removing takes this trade out of the record. Your holdings do not change, so if
                this row is what took the shares out of the book they will stay out, and the
                gain drops out of {trade.at.slice(0, 4)}&rsquo;s tax figures with it.
              </div>
              <div className="fab-row" style={{ marginTop: 9 }}>
                <button className="btn btn-block" onClick={() => setConfirmRemove(false)}>
                  Keep it
                </button>
                <button className="btn btn-danger btn-block" disabled={busy}
                        onClick={() => void remove()}>
                  Remove the trade
                </button>
              </div>
            </>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRemove(true)}>
              This trade should not be in the log
            </button>
          )}
        </>
      ) : null}
    </div>
  );
}
